import { Measurement, ParallelConfiguration } from '../shared/services/parser.service';
import { ParallelismMode } from '../shared/models/execution-details.models';
import { MissingSequentialBaselineError } from './baseline-utils';

/** Simple median calculation */
function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

export interface RegionMatrices {
  rows: string[];
  columns: string[];
  parallelConfigs: ParallelAxisConfig[];
  parallelismMode: ParallelismMode;
  times: number[][];
  seqTimes: number[];
  efficiencies: number[][];
  imbalances: number[][];
  speedups: number[][];
  speedupLosses: number[][];
  energies: EnergyMatrices[];
}

export interface EnergyMatrices {
  name: string;
  values: number[][];
}

export interface ParallelAxisConfig {
  key: string;
  label: string;
  order: number;
  idealParallelism: number;
  ranks: number | null;
  cores: number | null;
  totalCores: number;
  topologies: string[];
}

export type HybridViewMode = 'total-cores' | 'topology';

export interface BuildRegionOptions {
  hybridViewMode?: HybridViewMode;
}

export function buildRegion(regionId: string,
                            measurements: Measurement[],
                            options: BuildRegionOptions = {}): RegionMatrices {

  const region = measurements.filter(m => m.region === regionId);
  const rows    = [...new Set(region.map(m => m.workload))  ].sort((a, b) => a - b);
  const energySensorNames = [
    ...new Set(region.flatMap(m => (m.energyReadings ?? []).map(reading => reading.name)))
  ];
  const parallelismMode = region[0]?.parallelism.mode ?? 'unknown';
  const hybridViewMode = options.hybridViewMode ?? 'topology';
  const parallelConfigs = buildParallelAxisConfigs(region.map(m => m.parallelism), parallelismMode, hybridViewMode);
  const columns = parallelConfigs.map(config => config.label);
  const configIndexByKey = new Map(parallelConfigs.map((config, index) => [config.key, index]));

  // ---------- Group readings by (parallel configuration, workload) --------------------
  const byConfig = new Map<string, { times: number[], imbalances: number[], energies: Record<string, number[]> }>();
  region.forEach(m => {
    const configKey = resolveAxisConfigKey(m.parallelism, parallelismMode, hybridViewMode);
    const key = `${m.workload}|${configKey}`;
    const entry = byConfig.get(key) ?? { times: [], imbalances: [], energies: {} };
    entry.times.push(m.times.region);      // store ALL time repetitions
    if (typeof m.imbalance === 'number') { // Add imbalance only if it's a number
        entry.imbalances.push(m.imbalance);  // store ALL imbalance repetitions
    }
    (m.energyReadings ?? []).forEach(reading => {
      (entry.energies[reading.name] ??= []).push(reading.value);
    });
    byConfig.set(key, entry);
  });

  // ---------- Build time matrix (with median) ----------------
  const times: number[][] = rows.map(() => Array(columns.length).fill(NaN));

  rows.forEach((wl, i) => {
    parallelConfigs.forEach(config => {
      const j = configIndexByKey.get(config.key);
      if (j === undefined) return;

      const entry = byConfig.get(`${wl}|${config.key}`);
      if (!entry || !entry.times.length) return; // continue as NaN if no time data
      times[i][j] = median(entry.times);
    });
  });

  // ---------- Sequential times vector ----------------------------
  const idxSeq = parallelConfigs.findIndex(config => config.idealParallelism === 1);
  if (idxSeq === -1) {
    throw new MissingSequentialBaselineError(regionId, parallelismMode);
  }
  const seqTimes = times.map(r => r[idxSeq]);

  // ---------- Speedups (T1 / Tp) ---------------------------------
  // Stored as [workload][parallel configuration]
  const speedups = times.map((row, i) =>
    row.map(t => (seqTimes[i] > 0 && t > 0) ? seqTimes[i] / t : 0)
  );

  // ---------- Speedup Losses (Ideal - Real) ----------------------
  // Ideal is based on the effective parallel resources represented by the row label.
  const speedupLosses = speedups.map((row, i) =>
    row.map((realSpeedup, j) => parallelConfigs[j].idealParallelism - realSpeedup)
  );

  // ---------- Efficiencies -------------------------------------------
  const efficiencies = times.map((row, i) =>
    row.map((t, j) => seqTimes[i] / (parallelConfigs[j].idealParallelism * t))
  );

  // ---------- Imbalances (optional, with median) -----------------
  let imbalances: number[][] = [];
  const hasAnyImbalanceData = region.some(m => typeof m.imbalance === 'number' && m.imbalance !== 0);

  if (hasAnyImbalanceData) {
    imbalances = rows.map(() => Array(columns.length).fill(NaN));
    rows.forEach((wl, i) => {
      parallelConfigs.forEach(config => {
        const j = configIndexByKey.get(config.key);
        if (j === undefined) return;

        const entry = byConfig.get(`${wl}|${config.key}`);
        // Check if there's imbalance data and if the list is not empty
        if (entry && entry.imbalances.length > 0) {
          imbalances[i][j] = median(entry.imbalances);
          // Ex: imbalances[i][j] = median(entry.imbalances) * 100; 
        }
        // If there's no imbalance data for this cell, it will remain NaN.
      });
    });
  }

  const energies: EnergyMatrices[] = energySensorNames.map(sensorName => ({
    name: sensorName,
    values: rows.map(() => Array(columns.length).fill(NaN))
  }));

  energies.forEach(energyMatrix => {
    rows.forEach((wl, i) => {
      parallelConfigs.forEach(config => {
        const j = configIndexByKey.get(config.key);
        if (j === undefined) return;

        const samples = byConfig.get(`${wl}|${config.key}`)?.energies[energyMatrix.name] ?? [];
        if (samples.length > 0) {
          energyMatrix.values[i][j] = median(samples);
        }
      });
    });
  });

  return {
    rows: rows.map((_, i) => `i${i + 1}`),
    columns,
    parallelConfigs,
    parallelismMode,
    times,
    seqTimes,
    efficiencies,
    imbalances,
    speedups,
    speedupLosses,
    energies
  };
}

function buildParallelAxisConfigs(
  configurations: ParallelConfiguration[],
  parallelismMode: ParallelismMode,
  hybridViewMode: HybridViewMode
): ParallelAxisConfig[] {
  const uniqueConfigs = new Map<string, ParallelAxisConfig>();

  configurations.forEach(configuration => {
    const key = resolveAxisConfigKey(configuration, parallelismMode, hybridViewMode);
    const label = resolveAxisLabel(configuration, parallelismMode, hybridViewMode);
    const topologyLabel = buildTopologyLabel(configuration);
    const existing = uniqueConfigs.get(key);

    if (existing) {
      if (!existing.topologies.includes(topologyLabel)) {
        existing.topologies.push(topologyLabel);
        existing.topologies.sort();
      }
      return;
    }

    uniqueConfigs.set(key, {
      key,
      label,
      order: configuration.axisValue,
      idealParallelism: configuration.axisValue,
      ranks: configuration.ranks,
      cores: configuration.cores,
      totalCores: configuration.totalCores,
      topologies: [topologyLabel]
    });
  });

  return Array.from(uniqueConfigs.values()).sort((left, right) => {
    if (left.order !== right.order) {
      return left.order - right.order;
    }

    const rankDelta = (left.ranks ?? 0) - (right.ranks ?? 0);
    if (rankDelta !== 0) {
      return rankDelta;
    }

    const coreDelta = (left.cores ?? 0) - (right.cores ?? 0);
    if (coreDelta !== 0) {
      return coreDelta;
    }

    return left.key.localeCompare(right.key);
  });
}

function resolveAxisConfigKey(
  configuration: ParallelConfiguration,
  parallelismMode: ParallelismMode,
  hybridViewMode: HybridViewMode
): string {
  if (parallelismMode === 'hybrid' && hybridViewMode === 'total-cores') {
    return `hybrid-total:${configuration.totalCores}`;
  }

  return configuration.configKey;
}

function resolveAxisLabel(
  configuration: ParallelConfiguration,
  parallelismMode: ParallelismMode,
  hybridViewMode: HybridViewMode
): string {
  if (parallelismMode === 'hybrid' && hybridViewMode === 'total-cores') {
    return String(configuration.totalCores);
  }

  return configuration.axisLabel;
}

function buildTopologyLabel(configuration: ParallelConfiguration): string {
  if (configuration.mode !== 'hybrid') {
    return configuration.axisLabel;
  }

  return `${configuration.ranks ?? 0}x${configuration.cores ?? 0}`;
}
